import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BaseViewerLayer } from '@App/app/engine/viewer-layers/base-viewer-layer';
import { BasePlaneViewerLayer } from '@App/app/engine/viewer-layers/base-plane-viewer-layer';
import { BasePlane } from '@App/app/entities/layer/base-plane.model';
import { LAYER_TYPES } from '@App/app/entities/layer/enums/layer-types.enum';
import { LayerUI, LayerUiExpandable } from '@App/app/entities/layer/layer-ui.model';
import { ViewerModelTypes } from './models/viewer-layer-types.model';

@Injectable({
  providedIn: 'root',
})
export class ViewerLayerRegistryService {
  private viewerLayers: { [id: string]: BaseViewerLayer } = {};
  private _layersLoaded$ = new BehaviorSubject<boolean>(false);
  private _viewerLayerByTypes: ViewerModelTypes;
  layersLoaded$ = this._layersLoaded$.asObservable();

  constructor(private _injector: Injector) {
    import('./utils/viewer-layer-types.utils').then(({ VIEWER_MODEL_TYPES }) => {
      this._viewerLayerByTypes = VIEWER_MODEL_TYPES;
    });
  }

  getLayer(id: number | string): BaseViewerLayer | undefined {
    return this.viewerLayers[id];
  }

  exists(id: number | string): boolean {
    return id in this.viewerLayers;
  }

  initViewerLayer(layer: LayerUI | LayerUiExpandable, setAsLayerViewer = false): void {
    const viewerLayerType = this._viewerLayerByTypes[layer.type];

    if (viewerLayerType) {
      this.viewerLayers[layer.id] = new viewerLayerType(this._injector);
    }

    if (setAsLayerViewer) {
      this.viewerLayers[layer.id].layerViewer = layer;
    }

    if (layer.type === LAYER_TYPES.BASE_PLANE) {
      this._initBasePlane(layer as BasePlane);
    }
  }

  initViewerLayers(layers: (LayerUI | LayerUiExpandable)[]): void {
    for (const layer of layers) {
      this.initViewerLayer(layer);
    }
    this._layersLoaded$.next(true);
  }

  deleteLayer(id: number): void {
    this.viewerLayers[id]?.deleteLayer();
    delete this.viewerLayers[id];
  }

  deleteAllLocal(): void {
    Object.values(this.viewerLayers).forEach((layer) => layer.deleteLayer());
    this.viewerLayers = {};
    this._layersLoaded$.next(false);
  }

  getBasePlane(): BasePlaneViewerLayer | undefined {
    return Object.values(this.viewerLayers).find(
      (layer) => layer instanceof BasePlaneViewerLayer,
    ) as BasePlaneViewerLayer | undefined;
  }

  forEachLayer(callback: (layer: BaseViewerLayer, id: string) => void): void {
    Object.entries(this.viewerLayers).forEach(([id, layer]) => callback(layer, id));
  }

  private _initBasePlane(basePlane: BasePlane): void {
    this.getBasePlane()?.initBasePlane(basePlane);
  }
}
